import React, { useEffect, useState } from 'react';
import { Timer, Save, Trash2, ImagePlus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

// Admin control for the City Series "next drop" countdown (/city-series + /shop banner).
export default function NextDropCard() {
  const token = sessionStorage.getItem('admin_token');
  const [title, setTitle] = useState('');
  const [dropsAt, setDropsAt] = useState('');
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [teasers, setTeasers] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/city-series/next-drop`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await res.json();
        setTitle(d.config?.title || '');
        setDropsAt(toLocalInput(d.config?.drops_at));
        setActive(d.active);
        setTeasers(d.teasers || []);
      } catch (err) { console.error('Fetch next drop failed:', err); }
    })();
  }, [token]);

  const save = async (clear = false) => {
    setSaving(true);
    setMsg('');
    try {
      const body = clear
        ? { title: '', drops_at: '' }
        : { title, drops_at: dropsAt ? new Date(dropsAt).toISOString() : '' };
      const res = await fetch(`${API_URL}/api/admin/city-series/next-drop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      if (clear) { setTitle(''); setDropsAt(''); setActive(null); setMsg('Countdown cleared.'); }
      else { setActive(d.active); setMsg(d.active ? 'Countdown is LIVE on /city-series and /shop.' : 'Saved — date is in the past, so nothing shows.'); }
    } catch (err) {
      setMsg(String(err.message || err));
    } finally { setSaving(false); }
  };

  const uploadTeasers = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setMsg('');
    try {
      let imgs = teasers;
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await fetch(`${API_URL}/api/admin/city-series/teasers`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d.detail || 'Upload failed');
        imgs = d.images;
      }
      setTeasers(imgs);
      setMsg('Teasers updated — shown blurred under the countdown on /city-series.');
    } catch (err) {
      setMsg(String(err.message || err));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeTeaser = async (url) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/city-series/teasers?url=${encodeURIComponent(url)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setTeasers(d.images || []);
    } catch (err) { console.error('Remove teaser failed:', err); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="next-drop-card">
      <div className="flex items-center gap-2 mb-1">
        <Timer className="w-4 h-4 text-lime-600" />
        <h3 className="text-sm font-bold text-gray-900">Next Drop Countdown</h3>
        {active && <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-lime-100 text-lime-700 rounded-full">Live</span>}
      </div>
      <p className="text-xs text-gray-500 mb-4">Shows a live countdown + first-access waitlist on /city-series and the Shop banner until the date passes.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Drop 02 — International Series"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-lime-500"
          data-testid="next-drop-title-input"
        />
        <input
          type="datetime-local"
          value={dropsAt}
          onChange={(e) => setDropsAt(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-lime-500"
          data-testid="next-drop-date-input"
        />
        <button
          onClick={() => save(false)}
          disabled={saving || !dropsAt}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg disabled:opacity-50"
          data-testid="next-drop-save-btn"
        >
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button
          onClick={() => save(true)}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-500 text-xs font-bold rounded-lg disabled:opacity-50"
          data-testid="next-drop-clear-btn"
        >
          <Trash2 className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
      {msg && <p className="text-xs text-gray-600 mt-3" data-testid="next-drop-msg">{msg}</p>}

      <div className="mt-5 pt-4 border-t border-gray-100">
        <p className="text-xs font-bold text-gray-900 mb-1">Drop Reveal Teasers</p>
        <p className="text-xs text-gray-500 mb-3">Upload next-drop design shots — they show as blurred sneak-peek tiles under the countdown (max 6).</p>
        <div className="flex flex-wrap items-center gap-3">
          {teasers.map((url) => (
            <div key={url} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 group" data-testid="admin-teaser-thumb">
              <img src={`${API_URL}${url}`} alt="teaser" className="w-full h-full object-cover" />
              <button
                onClick={() => removeTeaser(url)}
                className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid="admin-teaser-remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-200 hover:border-lime-500 flex items-center justify-center cursor-pointer transition-colors" data-testid="admin-teaser-upload">
            <ImagePlus className={`w-5 h-5 ${uploading ? 'animate-pulse text-lime-500' : 'text-gray-400'}`} />
            <input type="file" accept="image/*" multiple className="hidden" onChange={uploadTeasers} disabled={uploading} />
          </label>
        </div>
      </div>
    </div>
  );
}
